import { PlayCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminLayout, { type NavItem } from '../../components/admin/AdminLayout';
import CoursePurchasesModal from '../../components/admin/CoursePurchasesModal';
import { TOUR, tourAttr } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLines, localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  courseApi,
  type CourseSalesLesson,
  type HostCourseSummary,
} from '../../services/courseApi';

const hostNavItems: NavItem[] = [
  { to: '/host/activities', label: 'Activities' },
  { to: '/host/courses', label: 'Courses' },
  { to: '/host/passes', label: 'Passes' },
  { to: '/host/bookings', label: 'Bookings' },
  { to: '/host/email', label: 'Email' },
];

function statusBadge(status: string): string {
  switch (status) {
    case 'APPROVED':
    case 'ACTIVE':
      return 'bg-green-100 text-green-700';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-700';
    case 'REJECTED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

type CourseTab = 'active' | 'review' | 'draft' | 'inactive';
const TAB_LABELS: Record<CourseTab, string> = {
  active: 'Active',
  review: 'In review',
  draft: 'Draft',
  inactive: 'Inactive',
};
function tabOf(status: string): CourseTab {
  if (status === 'APPROVED' || status === 'ACTIVE') return 'active';
  if (status === 'PENDING') return 'review';
  if (status === 'INACTIVE') return 'inactive';
  return 'draft';
}
function statusLabel(status: string): string {
  switch (status) {
    case 'APPROVED':
    case 'ACTIVE':
      return 'Live';
    case 'PENDING':
      return 'In review';
    case 'INACTIVE':
      return 'Offline';
    case 'REJECTED':
      return 'Rejected';
    default:
      return 'Draft';
  }
}

export default function HostCourses() {
  const siteLanguage = useSiteLanguage();
  const { getToken, user } = useAuth();
  const [courses, setCourses] = useState<HostCourseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CourseTab>('active');

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      setCourses(await courseApi.hostListCourses(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (course: HostCourseSummary) => {
    if (
      !confirm(
        `Delete "${localize(course.titleCN, course.titleEN, siteLanguage)}"? This cannot be undone.`
      )
    )
      return;
    try {
      setError(null);
      const token = await getToken();
      if (!token) return;
      await courseApi.hostDeleteCourse(course.id, token);
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete course');
    }
  };

  return (
    <AdminLayout
      title="Course Management"
      navItems={user?.isAdmin ? undefined : hostNavItems}
      headerRight={
        <Link
          to="/host/courses/new"
          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          {...tourAttr(TOUR.coursesCreate)}
        >
          <span className="mr-2">+</span>
          Create Course
        </Link>
      }
    >
      <div>
        {loading && <p className="text-gray-500">Loading...</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && (
          <>
            <div className="mb-6 rounded-lg bg-white shadow-sm">
              <div>
                <nav className="-mb-px flex overflow-x-auto" {...tourAttr(TOUR.coursesTabs)}>
                  {(['active', 'review', 'draft', 'inactive'] as const).map((tab) => {
                    const count = courses.filter((c) => tabOf(c.status) === tab).length;
                    return (
                      <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`flex-shrink-0 whitespace-nowrap border-b-2 px-6 py-3 text-sm font-medium ${
                          filter === tab
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        {TAB_LABELS[tab]}
                        <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {courses.filter((c) => tabOf(c.status) === filter).length === 0 ? (
              <div className="rounded-lg bg-white p-12 text-center shadow-sm">
                <div className="mb-4 flex justify-center text-gray-400">
                  <PlayCircle className="h-12 w-12" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">No online courses yet</h3>
                <p className="mb-6 text-gray-500">
                  Create your first online course to get started.
                </p>
                <Link
                  to="/host/courses/new"
                  className="inline-flex items-center rounded-lg bg-orange-500 px-4 py-2 text-white transition-colors hover:bg-orange-600"
                >
                  Create Course
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {courses
                  .filter((c) => tabOf(c.status) === filter)
                  .map((c) => (
                    <CourseCard
                      key={c.id}
                      course={c}
                      getToken={getToken}
                      onDelete={() => handleDelete(c)}
                    />
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

// Course card matching the Activities management layout: cover + status badge,
// titles, the price/lessons/sold summary, the collapsible per-lesson sales list
// (mirroring the session list on an activity card), and Edit / View / Delete.
function CourseCard({
  course: c,
  getToken,
  onDelete,
}: {
  course: HostCourseSummary;
  getToken: () => Promise<string | undefined>;
  onDelete: () => void;
}) {
  const siteLanguage = useSiteLanguage();
  const isPublic = c.status === 'APPROVED' || c.status === 'ACTIVE';
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm" {...tourAttr(TOUR.courseCard)}>
      <div className="relative h-40 bg-gray-200">
        {c.coverImage ? (
          <img
            src={c.coverImage}
            alt={localize(c.titleCN, c.titleEN, siteLanguage)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            No image
          </div>
        )}
        <span
          className={`absolute right-2 top-2 rounded px-2 py-1 text-xs font-medium ${statusBadge(c.status)}`}
        >
          {statusLabel(c.status)}
        </span>
      </div>

      <div className="p-4">
        <h3 className="mb-1 line-clamp-1 font-semibold text-gray-900">
          {bilingualLines(c.titleCN, c.titleEN, siteLanguage)[0]}
        </h3>
        {bilingualLines(c.titleCN, c.titleEN, siteLanguage)[1] && (
          <p className="mb-2 line-clamp-1 text-sm text-gray-500">
            {bilingualLines(c.titleCN, c.titleEN, siteLanguage)[1]}
          </p>
        )}

        <div className="mb-3 flex items-center gap-4 text-sm text-gray-500">
          <span>{formatUsd(c.price)}</span>
          <span>
            {c.lessonCount} {c.lessonCount === 1 ? 'lesson' : 'lessons'}
          </span>
          <span>{c.soldCount} sold</span>
        </div>

        {c.lessonCount > 0 && <LessonSales course={c} getToken={getToken} />}

        <div className="flex items-center gap-2">
          <Link
            to={`/host/courses/${c.id}/edit`}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-center text-sm transition-colors hover:bg-gray-50"
          >
            Edit
          </Link>
          {isPublic && (
            <Link
              to={`/courses/${c.slug}`}
              target="_blank"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors hover:bg-gray-50"
            >
              View
            </Link>
          )}
          <button
            onClick={onDelete}
            className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Collapsible per-lesson sales, matching the session list on an activity card:
// the header carries the summary plus the View action that opens the full sales
// report; expanding lists how many buyers each lesson has.
function LessonSales({
  course: c,
  getToken,
}: {
  course: HostCourseSummary;
  getToken: () => Promise<string | undefined>;
}) {
  const siteLanguage = useSiteLanguage();
  const [expanded, setExpanded] = useState(false);
  const [lessons, setLessons] = useState<CourseSalesLesson[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);

  // Loaded on first expand only: the list page would otherwise fire one report
  // request per course on mount.
  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next || lessons || loading) return;
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const report = await courseApi.hostCoursePurchases(c.id, token);
      setLessons(report.lessons);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between py-2 text-sm text-gray-500">
        <button
          onClick={toggle}
          className="flex flex-1 items-center gap-1 text-left hover:text-gray-700"
        >
          {/* The card already shows lessons/sold above, so name the section
              instead of repeating those numbers. */}
          <span>Sales by lesson</span>
          <svg
            className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {c.soldCount > 0 && (
          <button onClick={() => setShowReport(true)} className="text-blue-600 hover:underline">
            View
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-1 pb-2">
          {loading && <p className="px-2 py-1 text-xs text-gray-400">Loading...</p>}
          {error && <p className="px-2 py-1 text-xs text-red-600">{error}</p>}
          {lessons?.length === 0 && (
            <p className="px-2 py-1 text-xs text-gray-400">No lessons yet</p>
          )}
          {lessons?.map((lesson) => (
            <div
              key={lesson.order}
              className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 text-xs text-gray-700"
            >
              <span className="truncate">
                Lesson {lesson.order + 1} · {lesson.title}
              </span>
              <span className="whitespace-nowrap text-gray-600">
                {lesson.perLessonUnits + lesson.wholeCourseUnits} sold
              </span>
            </div>
          ))}
        </div>
      )}

      {showReport && (
        <CoursePurchasesModal
          activityId={c.id}
          courseTitle={localize(c.titleCN, c.titleEN, siteLanguage)}
          getToken={getToken}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}
